// 로그인 → 서버 허용 목록 확인(/api/me) → 화면 표시
import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Flex, Spin, Typography } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import { useApi, type Me } from './api-context';
import { useSession } from './session-context';

function Centered({ children }: { children: ReactNode }) {
  return (
    <Flex align="center" justify="center" style={{ minHeight: '100vh', padding: 16 }}>
      {children}
    </Flex>
  );
}

function LoginCard() {
  const { session } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const signIn = async () => {
    setError(null);
    setPending(true);
    try {
      await session.signIn();
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setError(code === 'auth/popup-blocked' ? '팝업이 차단되었습니다. 브라우저에서 팝업을 허용해 주세요.' : '로그인에 실패했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setPending(false);
    }
  };
  return (
    <Card style={{ width: 360, maxWidth: '100%' }}>
      <Typography.Title level={4}>아이소리 어드민</Typography.Title>
      <Typography.Paragraph type="secondary">등록된 Google 계정으로 로그인해 주세요.</Typography.Paragraph>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Button type="primary" icon={<GoogleOutlined />} block loading={pending} onClick={signIn}>
        Google로 로그인
      </Button>
    </Card>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { user, session } = useSession();
  const api = useApi();
  const me = useQuery({
    queryKey: ['me', user?.email ?? null],
    queryFn: () => api.get<Me>('/me'),
    enabled: !!user,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (user === undefined || (user && me.isPending)) {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    );
  }
  if (!user) {
    return (
      <Centered>
        <LoginCard />
      </Centered>
    );
  }
  if (me.isError) {
    const err = me.error instanceof ApiError ? me.error : null;
    const denied = err?.status === 403;
    return (
      <Centered>
        <Card style={{ width: 420, maxWidth: '100%' }}>
          <Alert
            type={denied ? 'warning' : 'error'}
            showIcon
            message={denied ? '접근 권한이 없습니다' : '서버 확인에 실패했습니다'}
            description={`${err?.message ?? '잠시 후 다시 시도해 주세요.'}${user.email ? ` (${user.email})` : ''}`}
          />
          <Flex gap={8} style={{ marginTop: 16 }}>
            {!denied && <Button onClick={() => me.refetch()}>다시 시도</Button>}
            <Button onClick={() => session.signOut()}>다른 계정으로 로그인</Button>
          </Flex>
        </Card>
      </Centered>
    );
  }
  return <>{children}</>;
}
